import { createStore } from 'zustand/vanilla';

const FISCAL_OVERRIDE_FIELDS = [
  'applies_vat_withholding',
  'applies_source_withholding',
  'is_self_withholding',
  'tax_regime',
  'applies_ica_withholding',
  'ica_withholding_type',
  'is_taxable',
  'is_withholding_agent',
  'is_gran_contribuyente',
  'tribute_type',
];

export const applyFiscalOverrides = (thirdParty) => {
  const overrides = thirdParty?.fiscal_config_overrides || {};
  const effective = { ...thirdParty };
  FISCAL_OVERRIDE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(overrides, field) && overrides[field] !== null) {
      effective[field] = overrides[field];
    }
  });
  return effective;
};

export const mergeFiscalOverrides = (currentOverrides, updateData) => {
  const merged = { ...(currentOverrides || {}) };
  FISCAL_OVERRIDE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(updateData, field)) {
      merged[field] = updateData[field];
    }
  });
  return merged;
};

const createThirdPartyStore = (supabaseClient, getUserId) => {
  if (!supabaseClient) {
    throw new Error('Supabase client is required');
  }
  if (!getUserId || typeof getUserId !== 'function') {
    throw new Error('getUserId function is required');
  }

  return createStore((set, get) => ({
    thirdParties: [],
    totalCount: 0,
    isLoading: false,
    error: null,
    isConnected: false,

    setConnectionStatus: (status) => set({ isConnected: status }),

    fetchThirdParties: async (page = 0, pageSize = 20, search = '') => {
      set({ isLoading: true, error: null });
      try {
        const userId = getUserId();
        let query = supabaseClient
          .from('invoice_third_parties')
          .select('*', { count: 'exact' })
          .eq('user_id', userId);

        if (search) {
          query = query.or(`nit.ilike.%${search}%,company.ilike.%${search}%,id_n.ilike.%${search}%,company_short.ilike.%${search}%`);
        }

        const from = page * pageSize;
        const to = from + pageSize - 1;

        const { data, error, count } = await query
          .order('company', { ascending: true })
          .range(from, to);

        if (error) throw error;

        set({
          thirdParties: (data || []).map(applyFiscalOverrides),
          totalCount: count || 0,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error fetching third parties:', error.message);
        set({ error: error.message, isLoading: false });
      }
    },

    updateThirdParty: async (id_n, dataToUpdate) => {
      set({ error: null });
      try {
        const userId = getUserId();
        
        const current = get().thirdParties.find((tp) => tp.id_n === id_n);
        const updateData = {
          ...dataToUpdate,
          fiscal_config_overrides: mergeFiscalOverrides(
            current?.fiscal_config_overrides,
            dataToUpdate
          ),
          updated_at: new Date().toISOString()
        };

        const { error } = await supabaseClient
          .from('invoice_third_parties')
          .update(updateData)
          .eq('id_n', id_n)
          .eq('user_id', userId);

        if (error) throw error;

        set((state) => ({
          thirdParties: state.thirdParties.map((tp) =>
            tp.id_n === id_n ? { ...tp, ...updateData } : tp
          ),
        }));
        
        return { success: true };
      } catch (error) {
        console.error('Error updating third party:', error.message);
        set({ error: error.message });
        return { success: false, error: error.message };
      }
    },

    bulkUpdateThirdParties: async (id_ns, updateData) => {
      set({ isLoading: true, error: null });
      try {
        const userId = getUserId();
        
        const selected = get().thirdParties.filter((tp) => id_ns.includes(tp.id_n));
        const updatedAt = new Date().toISOString();
        const updates = selected.map(async (tp) => {
          const fullUpdateData = {
            ...updateData,
            fiscal_config_overrides: mergeFiscalOverrides(
              tp.fiscal_config_overrides,
              updateData
            ),
            updated_at: updatedAt
          };
          const { error } = await supabaseClient
            .from('invoice_third_parties')
            .update(fullUpdateData)
            .eq('id_n', tp.id_n)
            .eq('user_id', userId);
          if (error) throw error;
          return { id_n: tp.id_n, fullUpdateData };
        });
        const completedUpdates = await Promise.all(updates);
        const updatesById = new Map(completedUpdates.map((item) => [item.id_n, item.fullUpdateData]));

        set((state) => ({
          thirdParties: state.thirdParties.map((tp) =>
            updatesById.has(tp.id_n) ? { ...tp, ...updatesById.get(tp.id_n) } : tp
          ),
          isLoading: false
        }));
        
        return { success: true };
      } catch (error) {
        console.error('Error in bulk update:', error.message);
        set({ error: error.message, isLoading: false });
        return { success: false, error: error.message };
      }
    }
  }));
};

export default createThirdPartyStore;
